import { forwardRef, useEffect, useRef, useState } from "react";
import SignaturePad from "react-signature-canvas";

import footerImg from "@/assets/images/footerImg.png";
import defaultLogo from "@/assets/images/headerImg.png";
import { WasteFormat } from "@/interfaces/wastetype.interface";
import { Docket } from "@/interfaces/workdocket.interface";
import { cn, formatToCapitalizedWords } from "@/lib/utils";
import { useGetWasteFormatQuery } from "@/redux/api/wasteManageSummaryandSettings";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";

interface DocketPrintPreviewDataTableProps {
  data: Docket;
  isExport?: boolean;
}

const DocketPrintPreviewDataTable = forwardRef<
  HTMLDivElement,
  DocketPrintPreviewDataTableProps
>(({ data, isExport }, ref): JSX.Element => {
  const { data: res } = useGetWasteFormatQuery({});
  const letterHead = res as WasteFormat;

  const sigCanvas = useRef<SignaturePad>(null);
  const [signDataUrl, setSignDataUrl] = useState<string>(data?.signature || "");

  // Load the initial signature if it exists
  useEffect(() => {
    if (sigCanvas.current && data?.signature) {
      sigCanvas.current.fromDataURL(data.signature);
      setSignDataUrl(data.signature); // Set the initial data URL
    }
  }, [data?.signature]);

  // Update the data URL whenever the signature changes
  const handleSignatureChange = (): void => {
    if (sigCanvas.current) {
      const dataUrl = sigCanvas.current?.toDataURL();
      setSignDataUrl(dataUrl);
    }
  };

  return (
    <section id='formatToPrint' className='my-4' ref={ref}>
      <div
        id='docketExportDetails'
        className={cn("shadow-md overflow-clip", !isExport && "border")}
      >
        {!isExport && letterHead?.headerActive && (
          <div className='border-b'>
            <img
              src={letterHead?.headerImage || defaultLogo}
              alt='formatLogo'
              className={cn(`w-full object-fill`, "h-[100px]")}
              style={{ opacity: letterHead?.headerOpacity }}
            />
          </div>
        )}
        <div
          id='docketDetails'
          className='my-4 ml-10 !overflow-hidden px-1'
          style={{
            marginTop: isExport ? "30px" : "",
          }}
        >
          <h1 className='text-xl font-semibold text-aldgray'>
            Work Docket 2.0
          </h1>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Report Type</span>
            <span className='leading-9'>Work Docket 2.0</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Work Docket 2.0 No.</span>
            <span className='leading-9'>{data.docketId}</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Date</span>
            <span className='leading-9'>
              {dateFormat(data.createdAt, "eee MMM d yyyy")}
            </span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Workers Name</span>
            <span className='leading-9'>--</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Customer Name</span>
            <span className='leading-9'>{data.customer?.name}</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Customer Address</span>
            <span className='leading-9'>{data.customer?.address?.label}</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Project Address</span>
            <span className='leading-9'>{data.project?.address?.label}</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Project Name</span>
            <span className='leading-9'>{data.project?.name}</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Project No</span>
            <span className='leading-9'>
              {data.project?.customerProjectReference ?? "--"}
            </span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Reference Number</span>
            <span className='leading-9'>
              {data.project?.ourReferenceNumber ?? "--"}
            </span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>
              Labour /Operator Service (Hours(S))
            </span>
            <span className='leading-9'>N/A</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Number Of Workers</span>
            <span className='leading-9'>{data.workersNo}</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Job Description</span>
            <span className='leading-9'>{data.jobDescription ?? "--"}</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Site Manager</span>
            <span className='leading-9'>{data.siteManager}</span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Declaration</span>
            <span className='leading-9'>
              By signing this docket, this customer irrevocably accepts the
              particulars as detailed on this docket as to the total hours and
              load or value shown.
            </span>
          </section>
          <section className='grid grid-cols-2 gap-6 ml-5'>
            <span className='leading-9 border-r'>Status</span>
            <span className='leading-9'>
              {formatToCapitalizedWords(data.status ?? "-")}
            </span>
          </section>
        </div>
        <section id='signature'>
          <div className='grid grid-cols-2 px-2 mt-24'>
            <span />
            <div className='font-semibold leading-9 text-center '>
              {signDataUrl && data?.signature && (
                <img
                  src={signDataUrl ?? data?.signature ?? ""}
                  alt='signature'
                  className='mb-1 border-b'
                  // className='absolute border-b -top-28 m4-2'
                />
              )}
              <span>Signature</span>
            </div>
          </div>
        </section>
        {!isExport && letterHead?.footerActive && (
          <img
            src={letterHead?.footerImage || footerImg}
            alt='footer'
            className={cn(`w-full object-fill`, "h-[100px]")}
            style={{ opacity: letterHead?.footerOpacity }}
          />
        )}
      </div>

      {/* this is to get the signature in data url so that pdf can export it */}
      <div className='hidden'>
        <SignaturePad
          ref={sigCanvas}
          penColor='black'
          canvasProps={{
            height: 200,
            width: 500,
          }}
          onEnd={handleSignatureChange}
        />
      </div>
    </section>
  );
});
DocketPrintPreviewDataTable.displayName = "DocketPrintPreviewDataTable";

export default DocketPrintPreviewDataTable;
